// Print a Talabya thermal receipt (80mm) using a hidden iframe + window.print().
// Works with any USB thermal printer (like Xprinter XP-80T) installed on the machine.
// For fully silent printing, launch the browser with the Kiosk-Printing flag:
//   Chrome/Edge: --kiosk-printing  (and set the Xprinter as default printer)
//
// Usage:
//   import { printReceipt, printCancelReceipt } from "@/utils/printReceipt";
//   await printReceipt(order);
//   await printCancelReceipt(order);   // small "CANCELLED" slip

import { buildReceiptHTML, buildCancelReceiptHTML } from "@/components/ReceiptTemplate";

/**
 * Renders the receipt inside a hidden iframe and triggers the printer.
 * @param {object} order   Order object as returned by /api/restaurant/orders
 * @param {object} [opts]  { paper: "58mm" | "80mm" } — defaults to 80mm.
 */
export async function printReceipt(order, opts = {}) {
  if (!order) return;
  const html = buildReceiptHTML(order, opts);
  return _printHtmlInIframe(html, "talabya-receipt-frame");
}

/**
 * Print a compact "CANCELLED" receipt when an order is cancelled. Only shows
 * the order number and daily invoice number — no items or customer details.
 */
export async function printCancelReceipt(order, opts = {}) {
  if (!order) return;
  const html = buildCancelReceiptHTML(order, opts);
  return _printHtmlInIframe(html, "talabya-cancel-frame");
}

function _printHtmlInIframe(html, frameId) {
  // Remove any previous iframe with the same id so we don't accumulate.
  const existing = document.getElementById(frameId);
  if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

  const iframe = document.createElement("iframe");
  iframe.id = frameId;
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("data-testid", frameId);
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.style.visibility = "hidden";
  document.body.appendChild(iframe);

  return new Promise((resolve) => {
    const cleanup = () => {
      setTimeout(() => {
        try { iframe.parentNode && iframe.parentNode.removeChild(iframe); } catch { /* noop */ }
        resolve();
      }, 800);
    };
    const doPrint = () => {
      try {
        const win = iframe.contentWindow;
        if (!win) return cleanup();
        const fontsReady = win.document.fonts?.ready || Promise.resolve();
        Promise.resolve(fontsReady).finally(() => {
          try { win.focus(); win.print(); } catch { /* noop */ }
          cleanup();
        });
      } catch { cleanup(); }
    };
    iframe.onload = () => setTimeout(doPrint, 120);
    iframe.srcdoc = html;
  });
}

// ---- LocalStorage helpers for auto-print preference (per browser/device) ----
const AUTO_PRINT_KEY = "talabya_auto_print_enabled";
const PRINTED_ORDERS_KEY = "talabya_printed_orders";

export function isAutoPrintEnabled() {
  try { return localStorage.getItem(AUTO_PRINT_KEY) === "1"; } catch { return false; }
}

export function setAutoPrintEnabled(enabled) {
  try { localStorage.setItem(AUTO_PRINT_KEY, enabled ? "1" : "0"); } catch { /* noop */ }
}

// Track which orders were already auto-printed so a refresh / websocket
// duplicate does not spam the printer.
export function wasAutoPrinted(orderId) {
  if (!orderId) return false;
  try {
    const raw = localStorage.getItem(PRINTED_ORDERS_KEY);
    const set = raw ? new Set(JSON.parse(raw)) : new Set();
    return set.has(orderId);
  } catch { return false; }
}

export function markAutoPrinted(orderId) {
  if (!orderId) return;
  try {
    const raw = localStorage.getItem(PRINTED_ORDERS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    if (!arr.includes(orderId)) arr.push(orderId);
    // Cap at 500 to avoid growing forever.
    const trimmed = arr.slice(-500);
    localStorage.setItem(PRINTED_ORDERS_KEY, JSON.stringify(trimmed));
  } catch { /* noop */ }
}
